import { makeAutoObservable } from 'mobx';

import { pureStructure } from '@cutwise/utils';

import {
  ControlMode,
  DEFAULT_HISTOGRAM_LIMIT,
  HistogramScale,
  PalettePreset,
  ShowModel,
  VisualMode,
  VisualModeBoth,
} from '@carbon/dm-industrial/lib/dm-industrial';
import { ClipPlaneMode } from '@carbon/lod3d-viewer/lib/lod3d-viewer';

const DEFAULT_SETTINGS = {
  // Common settings
  cameraSequence: [] as number[],

  // LOD 3d settings
  clipPlaneMode: 'off' as ClipPlaneMode,

  // DMIndustrial settings
  showModel: ShowModel.Both,
  visualMode: VisualMode.DISTANCE,
  visualModeBoth: VisualModeBoth.NEGATIVE_POSITIVE_2,
  palettePreset: PalettePreset.Rainbow,
  controlsMode: ControlMode.Translate,
  histogramScaleView: HistogramScale.Linear,
  isPaletteZeroPoint: true,
  switchModelsOnHistogram: false,
  switchModelsRecognition: false,
  histogramLowerValue: DEFAULT_HISTOGRAM_LIMIT,
  histogramUpperValue: DEFAULT_HISTOGRAM_LIMIT,
  histogramLowerLimit: DEFAULT_HISTOGRAM_LIMIT,
  histogramUpperLimit: DEFAULT_HISTOGRAM_LIMIT,
  paletteLowerBound: 0,
  paletteUpperBound: 1,

  scopeUpperValue: 1000,
  scopeLowerValue: -1000,
  lowerValuePD: -100,
  upperValuePD: 100,

  localTransform: {},

  histogramPlan: [0],
  histogramResult: [0],

  maxPoints: [0],

  maxPointDistances: [0],

  showMaxPoints: true,

  recoActual: false,

  fitness: -1,
};

export type ViewerSettingsOptions = typeof DEFAULT_SETTINGS;

export class ViewerSettings implements ViewerSettingsOptions {
  cameraSequence = [];

  clipPlaneMode!: ClipPlaneMode;

  showModel!: ShowModel;
  visualMode!: VisualMode;
  visualModeBoth!: VisualModeBoth;
  palettePreset!: PalettePreset;
  controlsMode!: ControlMode;
  histogramScaleView!: HistogramScale;
  isPaletteZeroPoint!: boolean;
  switchModelsOnHistogram!: boolean;
  switchModelsRecognition!: boolean;
  histogramLowerValue!: number;
  histogramUpperValue!: number;
  histogramLowerLimit!: number;
  histogramUpperLimit!: number;
  paletteLowerBound!: number;
  paletteUpperBound!: number;
  localTransform!: Record<string, number[]>;

  scopeUpperValue!: number;
  scopeLowerValue!: number;
  lowerValuePD!: number;
  upperValuePD!: number;

  histogramUpperLimitOrig!: number;
  histogramLowerLimitOrig!: number;

  enableMaxDistPoints!: boolean;

  recoActual!: boolean;

  fitness!: number;

  isHistogramActual!: boolean;
  histogramResult!: number[];
  histogramPlan!: number[];

  maxPoints!: number[];
  maxPointDistances!: number[];
  showMaxPoints!: boolean;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });

    this.reset();
  }

  get toJS(): ViewerSettingsOptions {
    return pureStructure(this);
  }

  apply(settings: Partial<ViewerSettingsOptions>) {
    (Object.keys(settings) as (keyof ViewerSettingsOptions)[]).forEach((key) => {
      const value = settings[key as keyof ViewerSettingsOptions];

      if (typeof value !== 'undefined' && value !== null) {
        let queryKey;
        const numericValue = +value;

        // when pass key from settings
        if (key in DEFAULT_SETTINGS) {
          queryKey = key;
        }

        if (queryKey && queryKey in this) {
          switch (queryKey) {
            case 'scopeUpperValue':
            case 'scopeLowerValue':
            case 'lowerValuePD':
            case 'upperValuePD':
              this[queryKey] = numericValue;
              break;
            default:
              this[queryKey] = value as never;

              break;
          }
        }
      }
    });
  }

  reset() {
    this.apply(DEFAULT_SETTINGS);
  }
}
